import { Monitor, Moon, Sun } from 'lucide-react';
import type { ReactNode } from 'react';

import { useTheme, type Theme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Theme-Toggle in der Topbar.
 *
 * Zeigt das Icon des aktuell aufgeloesten Themes (Sun / Moon). Im Menue kann
 * der User zwischen System, Hell und Dunkel waehlen. Auswahl wird per
 * `ThemeProvider` in `localStorage` persistiert.
 */
export function ThemeToggle(): JSX.Element {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const Icon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Theme wechseln"
                className="h-9 w-9"
              >
                <Icon className="h-4 w-4" />
                <span className="sr-only">Theme wechseln</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Theme wechseln</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel>Erscheinungsbild</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <ThemeItem current={theme} value="system" onSelect={setTheme}>
            <Monitor className="h-4 w-4" />
            <span>System</span>
          </ThemeItem>
          <ThemeItem current={theme} value="light" onSelect={setTheme}>
            <Sun className="h-4 w-4" />
            <span>Hell</span>
          </ThemeItem>
          <ThemeItem current={theme} value="dark" onSelect={setTheme}>
            <Moon className="h-4 w-4" />
            <span>Dunkel</span>
          </ThemeItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

function ThemeItem({
  current,
  value,
  onSelect,
  children,
}: {
  current: Theme;
  value: Theme;
  onSelect: (t: Theme) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <DropdownMenuCheckboxItem
      checked={current === value}
      onCheckedChange={() => onSelect(value)}
      className="gap-2"
    >
      {children}
    </DropdownMenuCheckboxItem>
  );
}
